<template>
  <ion-page>
    <ion-header class="music-library-folder-header">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button default-href="/music-library" text="" />
        </ion-buttons>
        <ion-title>{{ folderTitle }}</ion-title>
        <ion-buttons slot="end" v-if="showOfflineActions && !downloadProgressBar.downloading">
          <ion-button v-if="!folderOffline" fill="clear" @click="saveOffline" aria-label="Сохранить офлайн">
            <ion-icon :icon="cloudDownloadOutline" slot="icon-only" />
          </ion-button>
          <template v-else>
            <ion-icon :icon="cloudDone" class="toolbar-offline-done" aria-hidden="true" />
            <ion-button fill="clear" class="toolbar-remove-offline" @click="confirmRemoveFolderOffline" aria-label="Удалить из офлайна">
              <ion-icon :icon="trashOutline" slot="icon-only" />
            </ion-button>
          </template>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background-dark music-library-folder-content" :fullscreen="true">
      <div class="release-safe-area" />
      <div class="folder-meta-bar">
        <div class="duration-badge">
          <ion-icon :icon="timeOutline" />
          <span>{{ mediaUtils.formatTime(mediaUtils.getTotalPlaylistDuration(musicList)) }}</span>
        </div>
      </div>
      <div v-show="downloadProgressBar.downloading" class="library-download-progress">
        <div class="library-download-actions">
          <ion-button fill="outline" class="library-download-cancel" @click="stopLibraryMusicDownload">
            Отмена
          </ion-button>
          <ion-text class="library-download-meta ion-text-center">{{ downloadProgressBar.downloaded }} Мб</ion-text>
          <ion-text class="library-download-hint ion-text-center">Не закрывайте окно до завершения загрузки</ion-text>
        </div>
        <p v-if="downloadProgressBar.currentFileLabel" class="library-download-current-file ion-text-center">
          <span v-if="libraryDownloadFilePosition" class="library-download-file-index">{{ libraryDownloadFilePosition }}</span>
          {{ downloadProgressBar.currentFileLabel }}
        </p>
        <ion-progress-bar
          :value="libraryDownloadProgressValue"
          type="determinate"
          class="library-download-progress-bar"
        />
      </div>
      <div class="music-list-scroll">
        <ion-list class="bg-transparent music-library-track-list" lines="none">
          <ion-item
            v-for="(audioTrack, idx) in musicList"
            :key="audioTrack.id"
            class="music-item music-library-track-item"
            :class="{ playing: isTrackRowActive(idx) }"
            @click="selectTrack(idx)"
          >
            <div class="track-number">
              <span v-if="!isTrackRowActive(idx)">{{ idx + 1 }}</span>
              <ion-icon
                v-else
                :icon="playerBar.playing ? pause : play"
                class="playing-icon"
              />
            </div>
            <div class="track-info">
              <div class="track-name">{{ mediaUtils.getTrackName(audioTrack) }}</div>
              <div class="track-duration">{{ mediaUtils.formatTime(audioTrack.duration) }}</div>
            </div>
            <div class="track-actions">
              <ion-icon v-if="audioTrack.isOffline" :icon="cloudDone" class="offline-icon" />
              <div class="play-button-wrapper">
                <ion-icon
                  :icon="isTrackRowActive(idx) && playerBar.playing ? pause : play"
                  class="play-icon"
                />
              </div>
            </div>
          </ion-item>
        </ion-list>
        <p v-if="!musicList.length && !tracksLoading" class="ion-padding ion-text-center empty-tracks-hint">
          {{ emptyTracksHint }}
        </p>
        <ion-spinner v-if="tracksLoading" class="ion-margin tracks-spinner" name="crescent" />
        <div class="fake-footer" />
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { PlayerBar } from '@/model/playerbar.model';
import { GoogleMedia } from '@/model/googlemedia.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { MediaProgressBar } from '@/model/mediaprogressbar.model';
import { PlayerModes } from '@/model/player.mode';
import { Playlist } from '@/model/playlist.model';
import { libraryMusicOfflineDirName, libraryMusicService } from '@/services/library-music.service';
import MediaUtils from '@/services/media.utils';
import FilesystemCustomUtils from '@/services/filesystem.custom.utils';
import {
  alertController,
  toastController,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonPage,
  IonProgressBar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  isPlatform,
} from '@ionic/vue';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { RmxAudioPlayer } from 'capacitor-plugin-playlist';
import { cloudDone, cloudDownloadOutline, pause, play, timeOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
// @ts-ignore
import { MittEmitter } from 'mitt-emitter';
import defaultCover from '@/theme/liga-logo-white.png';

export default defineComponent({
  name: 'MusicLibraryFolder',
  components: {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonButtons,
    IonBackButton,
    IonButton,
    IonProgressBar,
    IonSpinner,
    IonText,
  },
  setup() {
    const playerBar = inject('playerBar') as PlayerBar;
    const audioPlayer = inject('audioPlayer') as RmxAudioPlayer;
    const emitter = inject('emitter') as MittEmitter;
    const globalContext = inject('globalContext') as GlobalContext;
    const mediaUtils = new MediaUtils();
    const filesystemCustomUtils = new FilesystemCustomUtils();
    return {
      playerBar,
      audioPlayer,
      emitter,
      globalContext,
      mediaUtils,
      filesystemCustomUtils,
      play,
      pause,
      timeOutline,
      cloudDownloadOutline,
      cloudDone,
      trashOutline,
    };
  },
  data() {
    return {
      folderId: 0,
      folderTitle: '',
      musicList: [] as GoogleMedia[],
      isFirstSelect: true,
      coverSrc: defaultCover,
      folderOffline: false,
      downloadProgressBar: new MediaProgressBar(),
      tracksLoading: true,
      downloadListener: null as null | ((ok?: boolean) => void),
      libraryDownloadEventName: '',
    };
  },
  computed: {
    isMobile(): boolean {
      return this.globalContext.isMobile;
    },
    showOfflineActions(): boolean {
      return (
        this.isMobile &&
        !this.globalContext.offlineMode &&
        (isPlatform('ios') || isPlatform('android'))
      );
    },
    emptyTracksHint(): string {
      if (this.globalContext.offlineMode) {
        return 'Нет сохранённых треков для этой папки.';
      }
      return 'Нет треков.';
    },
    libraryDownloadProgressValue(): number {
      const p = this.downloadProgressBar;
      if (!p.downloading || p.maxValue <= 0) return 0;
      if (p.percentage > 0) return Math.min(1, p.percentage / 100);
      return Math.min(1, p.currentValue / p.maxValue);
    },
    libraryDownloadFilePosition(): string {
      const p = this.downloadProgressBar;
      if (!p.downloading || p.maxValue <= 1) return '';
      const n = Math.min(p.currentValue + 1, p.maxValue);
      return `${n} / ${p.maxValue} · `;
    },
    expectedLibraryOfflineDirectory(): string {
      return `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${libraryMusicOfflineDirName(this.folderId)}`;
    },
  },
  ionViewWillEnter() {
    const id = Number(this.$route.params.folderId);
    this.folderId = id;
    this.playerBar.visible = this.playerBar.isReady && this.playerBar.musicList.length > 0;
    this.playerBar.currentMode = PlayerModes.EXTENDED;
  },
  ionViewDidEnter() {
    this.globalContext.isLoading = false;
    if (this.folderOffline && this.folderId) {
      this.$nextTick(() => this.syncPlayerBarIfThisFolderOffline());
    }
  },
  mounted() {
    this.loadTracks();
    this.isFirstSelect = true;
    this.playerBar.visible = false;
    this.playerBar.isReady = false;
  },
  ionViewWillLeave() {
    this.detachDownloadListener();
    this.playerBar.fixedAlbumArtUrl = '';
  },
  methods: {
    detachDownloadListener() {
      if (this.libraryDownloadEventName) {
        try {
          // mitt-emitter: off(topic) removes all handlers for this topic (unique per download id)
          this.emitter.off(this.libraryDownloadEventName);
        } catch {
          /* ignore */
        }
      }
      this.downloadListener = null;
      this.libraryDownloadEventName = '';
    },

    createUuid(): string {
      let dt = new Date().getTime();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    },

    async refreshFolderOfflineState() {
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative || !this.folderId) {
        this.folderOffline = false;
        return;
      }
      const dirName = libraryMusicOfflineDirName(this.folderId);
      const plistPath = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${dirName}/${this.filesystemCustomUtils.PLAYLIST_PATH}`;
      try {
        const res = await Filesystem.readFile({
          path: plistPath,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const raw = res.data as string;
        if (typeof raw !== 'string') {
          this.folderOffline = false;
          return;
        }
        const localPlaylist = JSON.parse(raw) as Playlist;
        if (!localPlaylist?.tracks?.length) {
          this.folderOffline = false;
          return;
        }
        this.folderOffline = await this.filesystemCustomUtils.isPlaylistOffline(
          this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
          localPlaylist
        );
        if (this.folderOffline) {
          this.musicList = this.musicList.map((t) => ({ ...t, isOffline: true }));
        }
      } catch {
        this.folderOffline = false;
      }
    },

    async loadTracksFromDisk() {
      const dirName = libraryMusicOfflineDirName(this.folderId);
      const plistPath = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${dirName}/${this.filesystemCustomUtils.PLAYLIST_PATH}`;
      try {
        const res = await Filesystem.readFile({
          path: plistPath,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const raw = res.data as string;
        const localPlaylist = typeof raw === 'string' ? (JSON.parse(raw) as Playlist) : null;
        if (!localPlaylist?.tracks?.length) {
          this.musicList = [];
          return;
        }
        this.musicList = localPlaylist.tracks;
        this.folderTitle = localPlaylist.name || 'Музыка';
        const allOff = await this.filesystemCustomUtils.isPlaylistOffline(
          this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
          localPlaylist
        );
        this.folderOffline = allOff;
        this.musicList = this.musicList.map((t) => ({ ...t, isOffline: allOff }));
      } catch (e) {
        console.error(e);
        this.musicList = [];
      }
    },

    isTrackRowActive(idx: number): boolean {
      return (
        !this.isFirstSelect &&
        this.playerBar.currentTrackIdx === idx &&
        !!(this.playerBar.visible || this.playerBar.microMode)
      );
    },

    async loadTracks() {
      const id = Number(this.$route.params.folderId);
      this.folderId = id;
      this.tracksLoading = true;
      if (!id) {
        this.tracksLoading = false;
        return;
      }

      const isNative = isPlatform('ios') || isPlatform('android');

      if (this.globalContext.offlineMode) {
        if (!isNative) {
          this.musicList = [];
          this.tracksLoading = false;
          return;
        }
        await this.loadTracksFromDisk();
        this.tracksLoading = false;
        return;
      }

      try {
        const tracks = await libraryMusicService.listUserTracks(id);
        this.musicList = tracks;
        const folders = await libraryMusicService.listUserFolders();
        const f = folders.find((x) => x.id === id);
        this.folderTitle = f ? f.title : 'Музыка';
        await this.refreshFolderOfflineState();
        this.$nextTick(() => this.syncPlayerBarIfThisFolderOffline());
      } catch (e) {
        console.error(e);
        this.musicList = [];
      } finally {
        this.tracksLoading = false;
      }
    },

    selectTrack(idx: number) {
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (
        !this.isFirstSelect &&
        this.playerBar.offlineDirectory === expectedDir &&
        this.playerBar.currentTrackIdx === idx
      ) {
        if (this.playerBar.playing) {
          this.emitter.emit('music-player-pause');
        } else {
          this.emitter.emit('music-player-play');
        }
        return;
      }

      if (this.audioPlayer && this.audioPlayer.isPlaying) {
        this.audioPlayer.pause();
      }
      this.playerBar.visible = true;
      this.playerBar.isFM = false;
      this.playerBar.currentTrackIdx = idx;
      this.playerBar.playlistName = this.folderTitle;
      this.playerBar.programShortName = '';
      this.playerBar.playlistImage = '';
      this.playerBar.playlistImageDir = '';
      this.playerBar.fixedAlbumArtUrl = this.coverSrc;
      this.playerBar.offline = this.folderOffline;
      this.playerBar.offlineDirectory = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${libraryMusicOfflineDirName(this.folderId)}`;

      if (this.isFirstSelect) {
        this.isFirstSelect = false;
        this.playerBar.musicList = this.musicList;
        this.emitter.emit('music-player-open-extended', true);
        this.emitter.emit('music-player-loaded-list', true);
      } else {
        this.emitter.emit('music-player-open-extended', true);
        this.emitter.emit('music-player-restart-and-play', true);
      }
    },

    async stopLibraryMusicDownload() {
      this.detachDownloadListener();
      this.downloadProgressBar.downloading = false;
      const pl = new Playlist(this.folderTitle, 'Музыка', 'LIB');
      pl.id = libraryMusicOfflineDirName(this.folderId);
      pl.name = this.folderTitle;
      pl.tracks = this.musicList;
      pl.isRelease = false;
      const utils = new FilesystemCustomUtils();
      await utils.cleanupPartialDownloads(pl);
      this.downloadProgressBar.currentValue = 0;
      this.downloadProgressBar.maxValue = 0;
      this.downloadProgressBar.downloaded = 0;
      this.downloadProgressBar.percentage = 0;
      this.downloadProgressBar.currentFileLabel = '';
    },

    syncPlayerBarIfThisFolderOffline() {
      if (!this.isMobile || !this.folderOffline) return;
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (this.playerBar.offlineDirectory !== expectedDir) return;
      this.playerBar.offline = true;
      this.playerBar.musicList = this.musicList.slice();
      this.emitter.emit('music-player-loaded-list', this.playerBar.playing);
    },

    async confirmRemoveFolderOffline() {
      const a = await alertController.create({
        header: 'Удалить офлайн',
        message: 'Удалить сохранённые файлы этой папки с устройства? Воспроизведение снова будет из сети.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.removeFolderOffline() },
        ],
      });
      await a.present();
    },

    async removeFolderOffline() {
      if (!this.folderId) return;
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative) return;
      await this.filesystemCustomUtils.removeLibraryMusicFolderOffline(this.folderId);
      this.folderOffline = false;
      this.musicList = this.musicList.map((t) => ({ ...t, isOffline: false }));
      const expectedDir = this.expectedLibraryOfflineDirectory;
      if (this.playerBar.offlineDirectory === expectedDir) {
        this.playerBar.offline = false;
        this.playerBar.musicList = this.musicList.slice();
        this.emitter.emit('music-player-loaded-list', this.playerBar.playing);
      }
      const toast = await toastController.create({
        message: 'Офлайн-копия папки удалена.',
        duration: 3000,
        color: 'dark',
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();
    },

    async saveOffline() {
      if (!this.musicList.length) return;
      const isNative = isPlatform('ios') || isPlatform('android');
      if (!isNative) {
        const a = await alertController.create({
          header: 'Офлайн',
          message: 'Сохранение доступно в мобильном приложении.',
          buttons: ['OK'],
        });
        await a.present();
        return;
      }

      this.detachDownloadListener();
      const progress = this.downloadProgressBar;
      progress.reset();
      progress.id = this.createUuid();
      this.libraryDownloadEventName = `downloaded-${progress.id}`;
      progress.downloading = true;
      progress.startDownload();

      const utils = new FilesystemCustomUtils();
      utils.setProgressBar(progress);
      utils.setEmitter(this.emitter);

      const handler = async (ok?: boolean) => {
        this.detachDownloadListener();
        if (ok === false) {
          const a = await alertController.create({
            header: 'Ошибка',
            message: 'Не удалось сохранить музыку офлайн. Проверьте сеть и попробуйте снова.',
            buttons: ['OK'],
          });
          await a.present();
          return;
        }
        await this.refreshFolderOfflineState();
        if (this.folderOffline) {
          this.musicList = this.musicList.map((t) => ({ ...t, isOffline: true }));
          this.syncPlayerBarIfThisFolderOffline();
          const toast = await toastController.create({
            message: 'Папка сохранена офлайн. Воспроизведение переключено на файлы устройства.',
            duration: 4000,
            color: 'dark',
            position: 'top',
            cssClass: 'custom-toast',
          });
          await toast.present();
        }
      };
      this.downloadListener = handler;
      this.emitter.on(this.libraryDownloadEventName, handler);

      try {
        await utils.removeLibraryMusicFolderOffline(this.folderId);
        await utils.storeLibraryMusicFolder(this.folderId, this.folderTitle, this.musicList);
      } catch (e) {
        console.error(e);
        this.downloadProgressBar.downloading = false;
        this.detachDownloadListener();
        const a = await alertController.create({
          header: 'Ошибка',
          message: 'Не удалось начать загрузку.',
          buttons: ['OK'],
        });
        await a.present();
      }
    },
  },
});
</script>

<style scoped>
.music-library-folder-header ion-toolbar {
  --background: linear-gradient(135deg, #e85d04 0%, #dc2f02 100%);
  --border-width: 0;
  --color: #fff;
  --min-height: 56px;
}

.music-library-folder-header ion-title {
  color: #fff !important;
}

.music-library-folder-header ion-back-button {
  --color: #fff;
  --icon-margin-start: 4px;
}

.library-download-progress {
  padding: 8px 16px 12px;
  width: 100%;
  box-sizing: border-box;
}
.library-download-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 8px;
}
.library-download-cancel {
  --border-color: rgba(255, 255, 255, 0.55);
  --color: #fff;
  flex-shrink: 0;
}
.library-download-meta,
.library-download-hint {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
  flex: 1 1 120px;
}
.library-download-hint {
  color: rgba(255, 255, 255, 0.65);
  flex: 1 1 200px;
}
.library-download-current-file {
  margin: 0 8px 10px;
  font-size: 0.78rem;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.88);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.library-download-file-index {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
}
.library-download-progress-bar {
  height: 5px;
  --progress-background: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.25);
  border-radius: 4px;
}

.toolbar-offline-done {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  margin-right: 4px;
}

.toolbar-remove-offline {
  --color: rgba(255, 255, 255, 0.92);
  margin-right: 4px;
}

.music-library-folder-content {
  --color: rgba(255, 255, 255, 0.92);
}

.release-safe-area {
  width: 100vw;
  height: var(--ion-safe-area-top);
}
.folder-meta-bar {
  display: flex;
  justify-content: center;
  padding: 10px 16px 6px;
  width: 100%;
  box-sizing: border-box;
}
.duration-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 1rem;
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
}
.music-library-track-list {
  padding: 4px 12px 8px;
  background: transparent !important;
  --background: transparent !important;
}

.music-list-scroll {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}
.fake-footer {
  height: 20vh;
}
.music-item {
  --background: rgba(255, 255, 255, 0.06);
  --background-hover: rgba(255, 255, 255, 0.1);
  --background-activated: rgba(255, 255, 255, 0.12);
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 60px;
  --color: rgba(255, 255, 255, 0.95);
  margin-bottom: 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.music-item::part(native) {
  border-radius: 14px;
}

.track-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.offline-icon {
  color: var(--ion-color-success);
  font-size: 1.15rem;
}

.playing-icon {
  color: var(--ion-color-orange) !important;
  font-size: 1.15rem;
}

.track-number {
  width: 28px;
  text-align: center;
  color: rgba(255, 255, 255, 0.72) !important;
}
.track-info {
  flex: 1;
  margin-left: 12px;
}
.track-name {
  color: #fff !important;
  font-weight: 500;
}
.track-duration {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55) !important;
}
.play-icon {
  font-size: 1.4rem;
  color: var(--ion-color-orange) !important;
}
.play-button-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(235, 89, 82, 0.18);
  border-radius: 50%;
}
.playing .play-button-wrapper {
  background: rgba(235, 89, 82, 0.28);
}
.playing .track-name {
  color: var(--ion-color-orange) !important;
}

.empty-tracks-hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.95rem;
}

.tracks-spinner {
  display: block;
  margin: 16px auto;
  color: var(--ion-color-orange);
}

@media (prefers-color-scheme: light) {
  .music-item {
    --background: rgba(255, 255, 255, 0.1);
    --background-hover: rgba(255, 255, 255, 0.14);
    --background-activated: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.14);
  }

  .duration-badge {
    background: rgba(255, 255, 255, 0.14);
  }
}
</style>
